import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FormikProps, useFormik } from 'formik';
import { Helmet } from 'react-helmet';

import { withErrorHandling } from '@selfserve/react-hocs';
import { Breadcrumb, CommonLayout } from '@selfserve/react-ui';
import {
  apiStatuses, ApplicationFeatures,
  billingCyclePayload,
  billingCycles,
  billingCycleType,
  BillingCycleTypes,
  buttonIds, ButtonType,
  IIndexSignature,
  PageType,
  planType,
  routesPaths,
  subscriptionStatuses, trackButtonClickEvent,
  trackPageLoadEvent
} from '@selfserve/react-utils';
import { storage, storageKeys, useStopBreakingPurchaseProcess, useStorage } from '@selfserve/react-hooks';

import { PromoCode, Summary } from './summary';
import { BillingCycle } from './billing-cycle';
import { YourPlan } from './your-plan';
import { AddOn } from './add-on';

import {
  StyledFormContainer,
  StyledPurchasePlanContainer,
  StyledPurchasePlanContentContainer,
  StyledSummaryContainer
} from '../ui';
import { planControllers } from '../constants';

import { PlanType, useBuildPlanRequest, usePackageDetailRequest } from '../data';

export interface FormValues {
  billingCycle: BillingCycleTypes;
  currency: string;
  partnerId: number;
  product: string;
  plan: PlanType;
  addons: IIndexSignature<PlanType>;
}

export const BuildYourPlan = withErrorHandling(({ openErrorModal }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { hash } = useParams();
  const [disabled, setDisabled] = useState(false);
  const { allowNavigation } = useStopBreakingPurchaseProcess(
    disabled,
    openErrorModal,
    [routesPaths.CHECKOUT.replace(':hash', hash as string)]
  );
  const [checkoutText, setCheckoutText] = useStorage(
    storage.SESSION,
    storageKeys.SUMMARY_CHECKOUT_TEXT,
    ''
  );
  const [countryCode] = useStorage(storage.SESSION, storageKeys.COUNTRY_CODE);
  const [, setPlanId] = useStorage(storage.SESSION, storageKeys.PROMO_PLAN_ID);
  const [, setSubscriptionId] = useStorage(
    storage.SESSION,
    storageKeys.SUBSCRIPTION_ID
  );
  const [, setSummary] = useStorage(
    storage.SESSION,
    storageKeys.SUMMARY_PRICES
  );
  const [, setPayload] = useStorage(
    storage.SESSION,
    storageKeys.SUMMARY_PAYLOAD
  );
  const [, setAppliedPromoCodes] = useStorage<PromoCode[]>(
    storage.SESSION,
    storageKeys.PROMO_CODES
  );
  const [, setPromoCodesToApply] = useStorage<string[]>(
    storage.SESSION,
    storageKeys.PROMO_CODES_APPLY,
    []
  );
  const [planSettings, setPlanSettings] = useStorage(
    storage.SESSION,
    storageKeys.PLAN_SETTINGS
  );
  const { data, isLoading } = usePackageDetailRequest(hash as string);
  const {
    data: buildPlanResponse,
    mutateAsync: handleBuildPlanRequest,
    isSuccess,
    isLoading: isBuildPlanLoading,
  } = useBuildPlanRequest(hash as string);
  const { data: packageDetail } = data || {};
  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    validateOnChange: false,
    initialValues: planSettings || {
      plan: { name: '', quantity: 1 },
      billingCycle: billingCycles.ANNUALLY,
      addons: {},
      currency: '',
      partnerId: 0,
      product: '',
    },
    onSubmit: () => undefined,
  });
  const { leading_plan, summary_text, checkout_cta_text, name } =
    packageDetail || {};
  const { addons } = formik.values;
  const isOnlyAddonsDisabled = useMemo(
    () =>
      leading_plan?.hasOwnProperty('visible') && !leading_plan?.visible
        ? !Object.keys(formik.values.addons || []).length
        : false,
    [leading_plan, addons]
  );
  const hasBillingCycleOptions = useMemo(() => {
    const currentPlan = packageDetail?.plans?.find(
      ({ plan_type }) => plan_type === planType.PLAN
    );
    return currentPlan?.pricing_info && currentPlan?.pricing_info?.length > 1;
  }, [packageDetail]);

  const handleError = useCallback((text: string) => {
    setDisabled(true);
    openErrorModal({ text });
  }, []);

  const handleRemoveAddon = useCallback(
    (key: string) => () => {
      const updatedAddons = { ...formik.values.addons };
      delete updatedAddons[key];
      formik.setFieldValue('addons', updatedAddons);
    },
    [formik.values]
  );

  const formToCalculatePayload = useCallback(() => {
    const { billingCycle, partnerId, addons, ...rest } = formik.values;
    const payload = {
      ...rest,
      billing_cycle: billingCyclePayload[billingCycle],
      partner_id: partnerId,
      addons: Object.values(addons),
    };
    return payload;
  }, [formik.values]);

  const getBillingCycleType = useCallback((billingCycle: BillingCycleTypes) => {
    switch (billingCycle) {
      default:
      case billingCycles.ANNUALLY:
        return billingCycleType.YEAR;
      case billingCycles.MONTHLY:
        return billingCycleType.MONTH;
    }
  }, []);

  const getAddonGroup = useCallback(
    (billingCycle: BillingCycleTypes) => {
      const { yearly_addons, monthly_addons } = packageDetail || {};
      switch (billingCycle) {
        default:
        case billingCycles.ANNUALLY:
          return yearly_addons?.addon_group;
        case billingCycles.MONTHLY:
          return monthly_addons?.addon_group;
      }
    },
    [packageDetail]
  );

  const getAddonPrices = useCallback(
    (billingCycle: BillingCycleTypes) => {
      const { plans } = packageDetail || {};
      const { billingCycle: currentBillingCycle, addons: currentAddons } =
        formik.values;
      return Object.values(currentAddons).map(({ name, quantity }) => {
        const { pricing_info, plan_type } =
          plans?.find((p) => p.name === name) || {};
        const { price } =
          (plan_type === planType.CHARGE
            ? pricing_info && pricing_info[0]
            : pricing_info?.find((p) => p.billing_cycle === billingCycle)) ||
          {};
        const addonsGroup = getAddonGroup(currentBillingCycle);
        const { group_info, group_items } =
          addonsGroup?.find(({ group_items }) =>
            group_items?.find((a) => a.name === name)
          ) || {};
        const addonItem = group_items?.find((a) => a.name === name);
        const secondaryName = group_info?.controllerValues
          ? group_info?.controllerValues?.find((v) => v.value === quantity)?.key
          : group_info?.controller === planControllers.COUNTER
          ? [quantity, addonItem?.headline].join(' ')
          : addonItem?.headline;
        return {
          name: group_info?.name,
          secondaryName,
          key: name,
          quantity,
          price,
        };
      });
    },
    [formik.values, getAddonGroup, packageDetail]
  );

  const getSummaryPrices = useCallback(() => {
    const { plans, leading_plan, description } = packageDetail || {};
    const { pricing_info } =
      plans?.find((p) => p.name === leading_plan?.name) || {};
    const {
      billingCycle: currentBillingCycle,
      plan: { quantity },
    } = formik.values;
    const billingCycle = getBillingCycleType(currentBillingCycle);
    const { price } =
      pricing_info?.find((p) => p.billing_cycle === billingCycle) || {};
    const addons = getAddonPrices(billingCycle);
    return {
      plan: {
        visible: !leading_plan?.hasOwnProperty('visible')
          ? true
          : leading_plan?.visible,
        name: description,
        quantity,
        price,
      },
      addons,
    };
  }, [formik.values, packageDetail, getAddonPrices, getBillingCycleType]);

  useEffect(() => {
    setAppliedPromoCodes([]);
    trackPageLoadEvent(
      PageType.View,
      'plan_overview_page',
      ApplicationFeatures.SUBSCRIPTION_FLOW,
      formik.values.product
    );
  }, []);

  useEffect(() => {
    const { status } = data || {};
    if (status === apiStatuses.NOT_FOUND) {
      openErrorModal({ text: t('common.purchase-error') });
      setDisabled(true);
    }
  }, [data, t, openErrorModal]);

  useEffect(() => {
    if (isSuccess) {
      const { status, data } = buildPlanResponse || {};
      if (status === apiStatuses.CREATED && data) {
        const { status: subscriptionStatus } = data || {};
        setSubscriptionId(data._id);
        allowNavigation(() => {
          if (
            subscriptionStatus === subscriptionStatuses.IN_TRIAL ||
            subscriptionStatus === subscriptionStatuses.ACTIVE
          ) {
            navigate(
              routesPaths.CONFIRMED.replace(
                ':subscriptionId',
                data._id as string
              )
            );
          } else {
            if (data?.coupon_ids?.length) setPromoCodesToApply(data.coupon_ids);
            setSummary(getSummaryPrices());
            setPayload(formToCalculatePayload());
            setPlanId(data._id);
            navigate(routesPaths.CHECKOUT.replace(':hash', hash as string));
          }
        });
      } else {
        const { id } = data || {};
        const errorTitleName = `error-titles.${id}`;
        const errorTextName = `errors.${id}`;
        const title = i18n.exists(errorTitleName)
          ? t(errorTitleName)
          : undefined;
        const text = i18n.exists(errorTextName)
          ? t(errorTextName).replace(
              ':productName',
              packageDetail?.description || ''
            )
          : undefined;
        openErrorModal({
          confirmBtnText: t('common.ok').toUpperCase(),
          title,
          text,
        });
      }
    }
  }, [
    buildPlanResponse,
    isSuccess,
    navigate,
    formToCalculatePayload,
    getSummaryPrices,
    setSummary,
    setPayload,
  ]);

  useEffect(() => {
    setPlanSettings(formik.values);
  }, [formik.values]);

  useEffect(() => {
    if (!checkoutText && checkout_cta_text) {
      setCheckoutText(checkout_cta_text);
    }
  }, [checkout_cta_text]);

  const handleBuildPlan = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      trackButtonClickEvent(
        ButtonType.Navigate,
        'plan_overview_continue',
        formik.values.plan.name,
        formik.values.billingCycle,
        ApplicationFeatures.SUBSCRIPTION_FLOW
      );
      if (!isBuildPlanLoading) {
        event.preventDefault();
        handleBuildPlanRequest(formToCalculatePayload());
      }
    },
    [formToCalculatePayload, isBuildPlanLoading]
  );

  useEffect(() => {
    trackButtonClickEvent(
      ButtonType.Toggle,
      'billing_cycle_toggle',
      formik.values.billingCycle,
      null,
      ApplicationFeatures.SUBSCRIPTION_FLOW
    );
  }, [formik.values.billingCycle]);

  return (
    <>
      <Helmet>
        <title>{t('titles.create-account')}</title>
      </Helmet>
      <CommonLayout>
        <StyledPurchasePlanContainer>
          <Breadcrumb />
          {!isLoading && (
            <StyledPurchasePlanContentContainer row fitHeight>
              <StyledFormContainer>
                {!disabled && hasBillingCycleOptions && (
                  <BillingCycle {...packageDetail} formik={formik} />
                )}
                <YourPlan
                  {...packageDetail}
                  formik={formik}
                  disabled={disabled}
                />
                {!disabled && <AddOn formik={formik} {...packageDetail} />}
              </StyledFormContainer>
              <StyledSummaryContainer>
                {!disabled && (
                  <Summary
                    packageName={name}
                    handleError={handleError}
                    buttonText={t('common.continue')}
                    buttonId={buttonIds.purchaseContinue}
                    summaryDescription={summary_text}
                    onClick={handleBuildPlan}
                    summary={getSummaryPrices()}
                    payload={formToCalculatePayload()}
                    handleRemoveAddon={handleRemoveAddon}
                    isLoading={isBuildPlanLoading || isLoading}
                    withoutDisabledStyles={!isOnlyAddonsDisabled}
                    disabled={isOnlyAddonsDisabled}
                    countryCode={countryCode}
                  />
                )}
              </StyledSummaryContainer>
            </StyledPurchasePlanContentContainer>
          )}
        </StyledPurchasePlanContainer>
      </CommonLayout>
    </>
  );
});
